import type * as React from 'react';
import { type UseFormReturn, useController } from 'react-hook-form';

import type { FontWeight } from '@gitbook/api-client';
import { Picker } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    FieldControllerProvider,
    FieldError,
    FieldGroup,
    Label,
    List,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemIndicator,
    type ListRenderItemInfo,
} from '@gitbook/spine-react';

import { DocumentationLink } from '../../../Link';
import type { FontFormData, FontSource } from './types';

/**
 * Font file weight picker component
 */
export function FontFilesList(props: {
    fontSources: FontSource[];
    setFontSources: (newSources: FontSource[]) => void;
    formControl: UseFormReturn<FontFormData>['control'];
}): React.ReactElement {
    const { fontSources, setFontSources, formControl } = props;

    const controller = useController({
        name: 'fontSources',
        control: formControl,
        rules: {
            validate: validateSources,
        },
    });

    const handleRemoveFile = (index: number) => {
        const updatedFiles = [...fontSources];
        updatedFiles.splice(index, 1);
        setFontSources(updatedFiles);
    };

    // Render a font file item
    const renderItem = ({ item, index }: ListRenderItemInfo<FontSource>) => {
        return (
            <ListItem>
                <ListItemIndicator>
                    <Icon.FileText className="size-4" />
                </ListItemIndicator>
                <ListItemBody>
                    <ListItemDescription>
                        {'file' in item
                            ? item.file.name
                            : getFileNameFromStorageKey(item.storageKey)}
                    </ListItemDescription>
                </ListItemBody>
                <ListItemControls when="always">
                    <div className="flex gap-2">
                        <Picker
                            className="shrink-0"
                            items={[
                                { value: 400, label: 'Regular' },
                                { value: 600, label: 'Semi-bold' },
                                { value: 700, label: 'Bold' },
                            ]}
                            value={item.weight}
                            onValueChange={(value: FontWeight) => {
                                setFontSources(
                                    fontSources.map((fontFile, idx) => {
                                        if (index === idx) {
                                            fontFile.weight = value;
                                        }

                                        return fontFile;
                                    })
                                );
                            }}
                        />
                        <Button
                            iconOnly
                            label="Delete"
                            leadingIcon={Icon.Trashcan}
                            kind="blank"
                            size="medium"
                            onPress={() => {
                                handleRemoveFile(index);
                            }}
                        />
                    </div>
                </ListItemControls>
            </ListItem>
        );
    };

    return (
        <FieldControllerProvider controller={controller}>
            <FieldGroup>
                <Label>Font files</Label>
                <Description>
                    Upload regular, bold, and (optionally) semi-bold font files.{' '}
                    <DocumentationLink
                        className="text-ui-medium"
                        path="publishing-documentation/customization#site-styles"
                    >
                        Learn more.
                    </DocumentationLink>
                </Description>
                <List
                    items={fontSources.map((fontFile, index) => ({
                        ...fontFile,
                        id: fontFile.storageKey,
                        index,
                    }))}
                    renderItem={renderItem}
                />

                <FieldError />
            </FieldGroup>
        </FieldControllerProvider>
    );
}

function getFileNameFromStorageKey(storageKey: string): string {
    return storageKey.split('/').pop() ?? '';
}

/**
 * Validate the font files to ensure we have one regular and (optionally) one bold weight.
 */
function validateSources(files: FontSource[]) {
    if (files.length === 0) {
        return 'Font files required.';
    }

    if (files.length > 3) {
        return 'Only regular, bold, and semi-bold fonts allowed.';
    }

    const regularFonts = files.filter((file) => file.weight === 400);
    if (regularFonts.length === 0) {
        return 'Regular font required.';
    }
    if (regularFonts.length > 1) {
        return 'Only one regular font allowed.';
    }

    const boldFonts = files.filter((file) => file.weight === 700);
    if (boldFonts.length === 0) {
        return 'Bold font required.';
    }
    if (boldFonts.length > 1) {
        return 'Only one bold font allowed.';
    }

    const semiboldFonts = files.filter((file) => file.weight === 600);
    if (semiboldFonts.length > 1) {
        return 'Only one semi-bold font allowed.';
    }

    return true;
}
